import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import badgerLogo from "@/assets/image.png";

const sidebarNavy = "#182737";

type BadgerAuthScreenVariant =
  | "loading"
  | "redirecting"
  | "error"
  | "msal-config";

type BadgerAuthScreenProps = {
  variant: BadgerAuthScreenVariant;
  title?: string;
  message?: string;
  error?: string | null;
  /** Shown under the spinner (e.g. redirect attempt count). */
  detail?: string;
  debugLines?: string[];
};

function getDefaultCopy(variant: BadgerAuthScreenVariant) {
  switch (variant) {
    case "redirecting":
      return {
        title: "Signing you in",
        message: "Redirecting to Microsoft…",
      };
    case "error":
      return {
        title: "Something went wrong",
        message: "We could not complete sign-in. Try again or contact support.",
      };
    case "msal-config":
      return {
        title: "Configuration required",
        message: "Authentication is not set up correctly for this environment.",
      };
    default:
      return {
        title: "Loading Badger",
        message: "Checking authentication…",
      };
  }
}

export function BadgerAuthScreen({
  variant,
  title,
  message,
  error,
  detail,
  debugLines,
}: BadgerAuthScreenProps) {
  const defaults = getDefaultCopy(variant);
  const showSpinner = variant === "loading" || variant === "redirecting";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        background: `
          radial-gradient(ellipse 80% 60% at 50% 0%, rgba(27, 122, 99, 0.12), transparent),
          radial-gradient(ellipse 70% 50% at 100% 100%, rgba(14, 70, 100, 0.08), transparent),
          linear-gradient(180deg, #f7f8fa 0%, #eef2f6 100%)
        `,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 400,
          p: 4,
          borderRadius: 3,
          textAlign: "center",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 12px 40px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1,
            mb: 3,
            borderRadius: 9999,
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            component="img"
            src={badgerLogo}
            alt=""
            sx={{ height: 40, width: "auto", display: "block" }}
          />
          <Typography
            sx={{
              color: sidebarNavy,
              fontWeight: 700,
              fontSize: "0.9rem",
              letterSpacing: "0.12em",
              lineHeight: 1,
            }}
          >
            BADGER
          </Typography>
        </Box>

        {showSpinner && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <CircularProgress
              size={44}
              thickness={4}
              sx={{ color: "primary.main" }}
            />
          </Box>
        )}

        <Typography variant="h6" fontWeight={700} gutterBottom>
          {title ?? defaults.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: error ? 2 : 0 }}>
          {message ?? defaults.message}
        </Typography>

        {detail && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {detail}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, textAlign: "left" }}>
            {error}
          </Alert>
        )}

        {debugLines && debugLines.length > 0 && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mt: 2,
              textAlign: "left",
              "&:before": { display: "none" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="caption" fontWeight={600}>
                Debug information
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {debugLines.map((line) => (
                <Typography
                  key={line}
                  variant="caption"
                  component="p"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all", mb: 0.5 }}
                >
                  {line}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {variant === "loading" && !error && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2.5 }}>
            If this takes too long, refresh the page.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
